import { CalendarDays, CalendarPlus, CalendarX2, MoreVertical, Phone, RotateCw, Search, Star } from "lucide-react";
import * as React from "react";
import { Toolbar } from "@/components/layout/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import type { DrawerMode } from "@/features/appointment-drawer";
import { AppointmentStatusLabel } from "@/features/appointment-status";
import type { ViewFilters } from "@/features/manage-view";
import { ApiError } from "@/lib/api";
import { SETTABLE_STATUSES, isClosed, listQuery, statusInfo, timeRange, type ListTab } from "@/lib/appointments";
import { callbackStatus, forSkilled, whoTakesCalendarCallback } from "@/lib/callbacks";
import { useAppointments, useCalendarCallbacks, useCalendars, useSetAppointmentStatus } from "@/lib/queries";
import type { AppointmentListItem, CalendarCallback } from "@/lib/types";
import { useDebounced } from "@/lib/use-debounced";
import { cn } from "@/lib/utils";

/**
 * The appointment list (ACL-292), as GoHighLevel's: upcoming, cancelled or all (newest first),
 * searched by title, narrowed by the same users and calendars as the calendar view and by Manage
 * view's days (ACL-598). The status changes in the row;
 * the row's menu opens the details or goes straight to rescheduling; cancelling asks first. Times
 * are on each appointment's calendar's clock.
 *
 * Callbacks sit among them by time (ACL-658). They are on no calendar and have no appointment status,
 * so narrowing to a calendar or a status leaves them out, and their times are on the viewer's clock.
 */
export function AppointmentList({
  filters, onOpen, onOpenCallback, onNew,
}: {
  filters: ViewFilters;
  onOpen: (id: number, mode?: DrawerMode) => void;
  onOpenCallback: (callback: CalendarCallback) => void;
  onNew: () => void;
}) {
  const toast = useToast();
  const [tab, setTab] = React.useState<ListTab>("upcoming");
  const [search, setSearch] = React.useState("");
  const q = useDebounced(search.trim());
  const [now] = React.useState(() => Date.now());
  const [cancelling, setCancelling] = React.useState<AppointmentListItem | null>(null);
  const setStatus = useSetAppointmentStatus();
  const { data: calendars = [] } = useCalendars();
  const byId = new Map(calendars.map((c) => [c.id, c]));

  const { nothing, past, ...query } = listQuery(tab, now, filters.statuses, filters);
  const dated = Boolean(filters.from || filters.to);
  const showAppointments = filters.type === "all" || filters.type === "appointments";
  const showCallbacks =
    (filters.type === "all" || filters.type === "callbacks") &&
    filters.calendarIds.length === 0 &&
    filters.statuses.length === 0;
  const { data, isPending, error, refetch } = useAppointments(
    {
      ...query,
      q: q || undefined,
      user_ids: filters.userIds.join(",") || undefined,
      calendar_ids: filters.calendarIds.join(",") || undefined,
      limit: 1000,
    },
    { enabled: showAppointments && !nothing },
  );
  const callbacks = useCalendarCallbacks(
    {
      start: query.start,
      end: query.end,
      user_ids: filters.userIds.join(",") || undefined,
      statuses: tab === "upcoming" ? "upcoming,due" : tab === "cancelled" ? "cancelled" : undefined,
      limit: 1000,
    },
    { enabled: showCallbacks && !nothing },
  );
  const needle = q.toLowerCase();
  const foundCallbacks =
    nothing || !showCallbacks
      ? []
      : (callbacks.data ?? []).filter(
          (c) => !needle || [c.contact_name, c.purpose].some((words) => words?.toLowerCase().includes(needle)),
        );
  const foundAppointments = nothing || !showAppointments ? [] : (data ?? []);
  const merged = [
    ...foundAppointments.map((a) => ({ at: Date.parse(a.starts_at), appointment: a, callback: null })),
    ...foundCallbacks.map((c) => ({ at: Date.parse(c.due_at), appointment: null, callback: c })),
  ].sort((a, b) => a.at - b.at);
  const rows = tab === "upcoming" ? merged : merged.reverse();
  const loading = !nothing && ((showAppointments && isPending) || (showCallbacks && callbacks.isPending));
  const failed = !nothing && showAppointments ? error : null;

  const change = (a: AppointmentListItem, status: string) =>
    setStatus.mutate(
      { id: a.id, status },
      {
        onSuccess: () => {
          setCancelling(null);
          toast.success(status === "cancelled" ? "Appointment cancelled" : `Marked ${statusInfo(status).label.toLowerCase()}`, a.title);
        },
        onError: (e) => toast.error("Could not change the status", e instanceof ApiError ? e.message : "Please try again."),
      },
    );

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-3 pb-3">
        <div className="flex items-center gap-1 border-b border-border" role="tablist">
          {(
            [
              ["upcoming", "Upcoming"],
              ["cancelled", "Cancelled"],
              ["all", "All"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              onClick={() => setTab(value)}
              className={cn(
                "-mb-px border-b-2 px-3 py-2 text-sm",
                tab === value ? "border-accent-9 font-medium text-accent-11" : "border-transparent text-text-muted hover:text-text",
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <Button onClick={onNew}>
          <CalendarPlus />
          New appointment
        </Button>
      </div>

      {filters.type === "blocks" ? (
        <Callout tone="info">The list shows callbacks and appointments. Time blocked off is on the calendar view.</Callout>
      ) : (
        <Card>
          <Toolbar>
            <Input
              className="w-64"
              leading={<Search className="size-3.5" />}
              placeholder="Search by title"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {!loading && <span className="ml-auto text-xs text-text-muted">{countText(foundAppointments.length, foundCallbacks.length)}</span>}
          </Toolbar>
          {loading ? (
            <TableSkeleton rows={6} cols={7} />
          ) : failed ? (
            <ErrorState
              error={failed}
              title="Could not load appointments"
              action={
                <Button variant="outline" onClick={() => void refetch()}>
                  Try again
                </Button>
              }
            />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={CalendarX2}
              title={q || filters.statuses.length || dated ? "No appointment matches" : tab === "upcoming" ? "Nothing booked ahead" : tab === "cancelled" ? "Nothing cancelled" : "No appointments"}
              description={
                q ? "Try another title."
                : past ? "Those days are over and Upcoming shows what is ahead. See them under All."
                : filters.statuses.length ? "Change the statuses in Manage view."
                : dated ? "Change the dates in Manage view."
                : "Book one with New appointment."
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Appointment time</TableHead>
                  <TableHead>Calendar</TableHead>
                  <TableHead>Appointment owner</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(({ appointment: a, callback }, i) => {
                  if (callback) return <CallbackRow key={`c${callback.id}`} index={i + 1} callback={callback} onOpen={onOpenCallback} />;
                  if (!a) return null;
                  const cal = byId.get(a.calendar_id);
                  const tz = cal?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
                  const closed = isClosed(a.status);
                  return (
                    <TableRow key={a.id} className={cn("[&>td]:h-14", closed && "text-text-muted")}>
                      <TableCell className="text-xs text-text-muted">{i + 1}</TableCell>
                      <TableCell>
                        <button type="button" className="flex max-w-56 items-start gap-2 text-left" onClick={() => onOpen(a.id)}>
                          <CalendarDays className="mt-0.5 size-4 shrink-0 text-text-muted" aria-hidden />
                          <span className={cn("line-clamp-2 font-medium hover:underline", closed ? "text-text-muted line-through" : "text-text")}>
                            {a.title}
                          </span>
                        </button>
                      </TableCell>
                      <TableCell className="max-w-44 truncate">
                        {a.attendee_count > 0 ? `${a.attendee_count} contact${a.attendee_count === 1 ? "" : "s"} (class)` : (a.contact_name ?? "—")}
                      </TableCell>
                      <TableCell>
                        {closed ? (
                          <Badge tone={statusInfo(a.status).tone}>{statusInfo(a.status).label}</Badge>
                        ) : (
                          <Select
                            value={a.status}
                            disabled={setStatus.isPending}
                            onValueChange={(next) => (next === "cancelled" ? setCancelling(a) : change(a, next))}
                          >
                            <SelectTrigger className="h-8 w-36" aria-label={`Status of ${a.title}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {SETTABLE_STATUSES.map((s) => (
                                <SelectItem key={s} value={s}>
                                  <AppointmentStatusLabel status={s} />
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {a.rescheduled_from_id && (
                          <Badge tone="outline" size="sm" className="mb-1">
                            Rescheduled
                          </Badge>
                        )}
                        <span className="block whitespace-nowrap">{timeRange(a.starts_at, a.ends_at, tz, cal?.time_format ?? "12h", cal?.locale)}</span>
                      </TableCell>
                      <TableCell className="text-text-muted">{a.calendar_name}</TableCell>
                      <TableCell className="text-text-muted">{a.owner_name ?? "—"}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${a.title}`}>
                              <MoreVertical />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => onOpen(a.id)}>View details</DropdownMenuItem>
                            {!closed && (
                              <>
                                <DropdownMenuItem onSelect={() => onOpen(a.id, "reschedule")}>Reschedule</DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem destructive onSelect={() => setCancelling(a)}>
                                  Cancel appointment
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      <ConfirmDialog
        open={cancelling !== null}
        onOpenChange={(open) => !open && setCancelling(null)}
        title="Cancel this appointment?"
        description={
          <>
            <span className="font-medium">{cancelling?.title}</span>: its time is freed for other bookings, and it stays in
            the history as cancelled.
          </>
        }
        confirmLabel="Cancel appointment"
        cancelLabel="Keep it"
        destructive
        loading={setStatus.isPending}
        onConfirm={() => cancelling && change(cancelling, "cancelled")}
      />
    </>
  );
}

function countText(appointments: number, callbacks: number): string {
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
  if (callbacks === 0) return plural(appointments, "appointment");
  return appointments === 0
    ? plural(callbacks, "callback")
    : `${plural(appointments, "appointment")}, ${plural(callbacks, "callback")}`;
}

/** A callback among the appointments: its details open from it, and there is nothing to set here,
 *  since an outcome ends a callback. */
function CallbackRow({
  index, callback: c, onOpen,
}: {
  index: number;
  callback: CalendarCallback;
  onOpen: (callback: CalendarCallback) => void;
}) {
  const stands = callbackStatus(c, Date.now());
  const closed = c.status === "cancelled" || c.status === "done";
  const Icon = c.backup_of ? RotateCw : forSkilled(c) ? Star : Phone;
  const when = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(c.due_at));
  const title = c.purpose ?? "Callback";
  return (
    <TableRow className={cn("[&>td]:h-14", closed && "text-text-muted")}>
      <TableCell className="text-xs text-text-muted">{index}</TableCell>
      <TableCell>
        <button type="button" className="flex max-w-56 items-start gap-2 text-left" onClick={() => onOpen(c)}>
          <Icon className="mt-0.5 size-4 shrink-0 text-purple-11" aria-hidden />
          <span className={cn("line-clamp-2 font-medium hover:underline", c.status === "cancelled" ? "text-text-muted line-through" : "text-text")}>
            {title}
          </span>
        </button>
      </TableCell>
      <TableCell className="max-w-44 truncate">{c.contact_name ?? "—"}</TableCell>
      <TableCell>
        <Badge tone={stands.tone}>{stands.label}</Badge>
      </TableCell>
      <TableCell className="text-xs">
        <span className="block whitespace-nowrap">{when}</span>
      </TableCell>
      <TableCell className="text-text-muted">Callback</TableCell>
      <TableCell className="text-text-muted">{whoTakesCalendarCallback(c)}</TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${title}`}>
              <MoreVertical />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onOpen(c)}>View details</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}
